/**
 * Sampled all-rating web-vitals events, so p75 per page template is computable.
 *
 * The reporter used to send only threshold breaches (needs-improvement and
 * poor) to the owned beacon: 1,889 breach events against 4,397 SEO page views
 * over 28 Aug-27 Sep 2026. A breach rate per template is computable from that,
 * a p75 is not, and CrUX has no URL-level data at this traffic. One page load
 * in five now also sends every metric it produces, whatever its rating,
 * labelled with the route template. dbt model v_web_vitals_p75_by_template_weekly
 * computes weekly p75 from these rows.
 */

export const WEB_VITALS_SAMPLE_EVENT = "web_vitals_sample";
export const WEB_VITALS_SAMPLE_RATE = 0.2;

type RouteParams = Readonly<Record<string, string | string[] | undefined>> | null | undefined;

function segmentsOf(pathname: string): string[] {
  return pathname.split("/").filter(Boolean);
}

function decoded(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function replaceRun(segments: string[], values: readonly string[], label: string): string[] {
  if (!values.length) return segments;
  for (let start = segments.length - values.length; start >= 0; start -= 1) {
    const run = segments.slice(start, start + values.length).map(decoded);
    if (run.every((segment, index) => segment === values[index])) {
      return [...segments.slice(0, start), label, ...segments.slice(start + values.length)];
    }
  }
  return segments;
}

/**
 * The App Router template of the current page: each dynamic segment's value is
 * replaced by its parameter name, so /rashifal/aries/ becomes
 * /rashifal/[rashi]/ and every sign's samples pool into one p75. Literal
 * routes (params empty) keep their path.
 */
export function routeTemplate(pathname: string, params: RouteParams): string {
  let segments = segmentsOf(pathname);
  // Longest values first, so a catch-all run is placed before any single value
  // it contains.
  const entries = Object.entries(params ?? {})
    .map(([name, value]) => [name, value] as const)
    .filter((entry): entry is readonly [string, string | string[]] => entry[1] !== undefined)
    .sort(([, a], [, b]) => (Array.isArray(b) ? b.length : 1) - (Array.isArray(a) ? a.length : 1));
  for (const [name, value] of entries) {
    segments = Array.isArray(value)
      ? replaceRun(segments, value, `[...${name}]`)
      : replaceRun(segments, [value], `[${name}]`);
  }
  const template = segments.length ? `/${segments.join("/")}/` : "/";
  return template.slice(0, 256);
}

/** Decided once per page load, so all metrics of a load are in or out together. */
export function isSampledPageLoad(random: () => number = Math.random): boolean {
  return random() < WEB_VITALS_SAMPLE_RATE;
}
